import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../utils/streamApi';
import { isAdmin } from '../utils/entitlements';
import PasswordInput from './PasswordInput';
import '../css/camera-stream-settings.css';

// Main + sub stream settings over ONVIF (resolution, frame rate, frame (I-frame)
// interval, bitrate). Used in Guided Setup right after a camera is added and in
// Settings → Camera → Stream.
//
// ONVIF login: the backend tries the camera's own login first. Only when the
// camera rejects it does this ask for a different user/password — and it stops
// offering retries after two rejections, because many cameras lock the account
// (and drop the video stream) after a few bad logins.
//
// Nothing is changed on the camera unasked: the card ASKS "Optimise this camera
// for Parvekshak?" with Optimise / Keep as it is, because the change is made
// inside the camera and other apps or recorders using it see it too. "Keep as
// it is" is remembered; Optimise stays one click away. After an Optimise it
// shows before → after with a way to put the old settings back.
//
// readOnly: just show the settings (Debug popup) — no Change/Apply, no password
// box, never writes to the camera.

const MAX_TRIES = 2;

const fmtRes = s => `${s.width}×${s.height}`;
const line   = s => s ? `${fmtRes(s)} · ${s.fps} fps · interval ${s.gop}` : '—';
const pick   = s => ({ width: s.width, height: s.height, fps: s.fps, gop: s.gop, bitrate: s.bitrate });
const ROLE   = { main: 'Main stream (recording)', sub: 'Sub stream (live view)' };

function StreamEditor({ role, value, options, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const res = options.resolutions || [];
  const [fMin, fMax] = options.fps || [1, 30];
  // ONVIF's advertised minimum can be too high (CP Plus says 25, really accepts
  // down to the frame rate), so only the maximum is enforced here.
  const gMax = (options.gop || [1, 250])[1];
  const fpsList = [];
  for (let f = fMin; f <= fMax; f++) fpsList.push(f);
  return (
    <div className="css-edit">
      <div className="css-edit-title">{ROLE[role] || role}</div>
      <div className="css-edit-grid">
        <label>Resolution
          <select value={fmtRes(value)} onChange={e => {
            const [w, h] = e.target.value.split('×').map(Number);
            onChange({ ...value, width: w, height: h });
          }}>
            {res.map(r => <option key={fmtRes(r)} value={fmtRes(r)}>{fmtRes(r)}</option>)}
          </select>
        </label>
        <label>Frame rate
          <select value={value.fps} onChange={e => set('fps', Number(e.target.value))}>
            {fpsList.map(f => <option key={f} value={f}>{f} fps</option>)}
          </select>
        </label>
        <label>Frame interval
          <input type="number" min={1} max={gMax} value={value.gop}
            onChange={e => set('gop', Number(e.target.value))} />
          <span className="css-range">frames, up to {gMax}</span>
        </label>
        <label>Bitrate (kbps)
          <input type="number" min={64} max={16384} step={64} value={value.bitrate}
            onChange={e => set('bitrate', Number(e.target.value))} />
        </label>
      </div>
    </div>
  );
}

// A camera answers each ONVIF call within 8 s, but applying settings is a chain of
// them (read, set main, set sub, read back) plus the stream reconnecting — 10–30 s,
// longer on a slow camera. Say what is happening and for how long, so a slow
// camera doesn't look like a hung page, and give up after BUSY_LIMIT_S.
const BUSY_LIMIT_S = 90;
const BUSY_TEXT = {
  apply: 'Updating the camera’s settings… This usually takes 10–30 seconds, and the picture may drop for a moment.',
  read:  'Asking the camera for its stream settings…',
  login: 'Checking the ONVIF login with the camera…',
  keep:  'Saving…',
};
const TOO_SLOW = 'The camera is taking too long to answer. Check that it is online, then try again.';

// apiFetch with a time limit: an AbortError here means the camera never answered.
async function fetchLimited(path, opts = {}) {
  const ac = new AbortController();
  const t  = setTimeout(() => ac.abort(), BUSY_LIMIT_S * 1000);
  try { return await apiFetch(path, { ...opts, signal: ac.signal }); }
  finally { clearTimeout(t); }
}
const failText = e => (e?.name === 'AbortError' ? TOO_SLOW : 'Cannot reach server');

// onState(idx, { ok, needs }) — needs: the camera could be optimised and nobody
// has answered yet (Guided Setup's "Optimise all" and its reminder).
// refreshKey: change it to make the card read the camera again.
export default function CameraStreamSettings({ idx, readOnly = false, onState, refreshKey }) {
  const [state,   setState]   = useState(null);   // GET /cameras/{idx}/onvif response
  const [busy,    setBusy]    = useState('');     // '' | 'read' | 'apply' | 'login'
  const [msg,     setMsg]     = useState(null);   // { ok, text }
  const [editing, setEditing] = useState(null);   // {main, sub} draft values
  const [cred,    setCred]    = useState({ user: '', password: '' });
  const [tries,   setTries]   = useState(0);
  const [elapsed, setElapsed] = useState(0);      // seconds the current camera call has taken
  const poll = useRef(null);

  useEffect(() => {
    if (!busy) return undefined;
    setElapsed(0);
    const t0 = Date.now();
    const tick = setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(tick);
  }, [busy]);
  const progress = busy ? (
    <p className="css-progress" role="status">
      <span className="css-spin" aria-hidden="true" /> {BUSY_TEXT[busy]} <b>{elapsed} s</b>
    </p>
  ) : null;

  const streamsOf = st => Object.fromEntries((st?.streams || [])
    .filter(s => s.role === 'main' || s.role === 'sub').map(s => [s.role, s]));

  const apply = async body => {
    setBusy('apply'); setMsg(null);
    try {
      const res  = await fetchLimited(`/cameras/${idx}/onvif/streams`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.ok) {
        setState(data); setEditing(null);
        // "ok" only means the camera answered. Say "Applied" only when it also
        // KEPT the values (backend `kept`); otherwise the red "didn't keep" note
        // below is the whole story — a green line beside it read as success.
        const notKeptNow = Object.values(data.applied?.kept || {}).some(v => v === false);
        setMsg(notKeptNow ? null
          : { ok: true, text: 'Applied — the camera may take a few seconds to reconnect.' });
      } else {
        setMsg({ ok: false, text: data.error || data.detail || 'The camera did not accept the settings' });
        if (data.kind === 'auth') setState(data);
      }
    } catch (e) { setMsg({ ok: false, text: failText(e) }); }
    finally { setBusy(''); }
  };

  const load = async (retry = false) => {
    setBusy('read'); setMsg(null);
    try {
      const res  = await fetchLimited(`/cameras/${idx}/onvif${retry ? '?retry=1' : ''}`);
      const data = await res.json();
      setState(data);
      if (data.kind === 'auth') setCred(c => ({ ...c, user: c.user || data.user || 'admin' }));
      // Backend still applying the recommended settings to a just-added camera.
      if (data.ok && data.applied?.status === 'pending') {
        clearTimeout(poll.current);
        poll.current = setTimeout(() => load(), 3000);
      }
    } catch (e) { setState({ ok: false, kind: 'error', error: failText(e) }); }
    setBusy('');
  };

  useEffect(() => {
    load();
    return () => clearTimeout(poll.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, refreshKey]);

  useEffect(() => {
    if (!onState || !state) return;
    onState(idx, { ok: !!state.ok,
                   needs: !!state.ok && !state.matches_recommended && state.applied?.preset !== 'kept',
                   // tried, and the camera didn't keep it — "Optimise all" would
                   // only repeat what already failed
                   refused: Object.values(state.applied?.kept || {}).some(v => v === false) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const keep = async () => {
    setBusy('keep'); setMsg(null);
    try {
      const r = await fetchLimited(`/cameras/${idx}/onvif/streams/keep`, { method: 'PUT' });
      const d = await r.json();
      if (d.ok) setState(st => ({ ...st, applied: d.applied }));
      else setMsg({ ok: false, text: d.detail || 'Could not save that' });
    } catch (e) { setMsg({ ok: false, text: failText(e) }); }
    finally { setBusy(''); }
  };

  const login = async () => {
    if (!cred.password) { setMsg({ ok: false, text: 'Enter the ONVIF password' }); return; }
    setBusy('login'); setMsg(null);
    try {
      const res  = await fetchLimited(`/cameras/${idx}/onvif/credentials`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cred),
      });
      const data = await res.json();
      setState(data);
      if (data.ok) {
        setTries(0); setCred({ user: '', password: '' });
        // The backend finishes a stalled automatic optimise as part of this call.
        if (data.notes) setMsg({ ok: true, text: 'Applied — the camera may take a few seconds to reconnect.' });
        setBusy('');
        return;
      }
      setTries(t => t + 1);
    } catch (e) { setMsg({ ok: false, text: failText(e) }); }
    setBusy('');
  };

  const startEdit = () => {
    const cur = streamsOf(state);
    setEditing(Object.fromEntries(Object.entries(cur).map(([r, s]) => [r, pick(s.current)])));
  };

  // ── render ──
  if (!state) {
    return <div className="css-box">{progress || <span className="css-muted">Reading stream settings…</span>}</div>;
  }

  if (!state.ok && state.kind === 'auth') {
    const locked = tries >= MAX_TRIES;
    return (
      <div className="css-box warn">
        <div className="css-title">⚠️ Stream settings need the camera’s ONVIF password</div>
        <p className="css-muted">
          The camera didn’t accept its login for ONVIF. Some cameras (CP Plus, Dahua) use a
          separate ONVIF user — in the camera’s app: System → Account → ONVIF User.
        </p>
        {state.attempts_left != null && (
          <p className="css-bad">The camera says {state.attempts_left} attempt(s) left before it locks.</p>
        )}
        {readOnly ? (
          <p className="css-muted">Enter it in Settings → Camera → Stream.</p>
        ) : locked ? (
          <p className="css-bad">
            Stopped after {MAX_TRIES} wrong passwords so the camera doesn’t lock you out.
            Check the password in the camera’s own app, then come back to Settings → Camera.
          </p>
        ) : (
          <div className="css-login">
            <input placeholder="User" value={cred.user} autoComplete="off"
              onChange={e => setCred({ ...cred, user: e.target.value })} />
            <PasswordInput placeholder="ONVIF password" value={cred.password} autoComplete="new-password"
              onChange={e => setCred({ ...cred, password: e.target.value })} />
            <button className="css-btn primary" onClick={login} disabled={!!busy}>
              {busy === 'login' ? 'Checking…' : 'Try'}
            </button>
          </div>
        )}
        {progress}
        {msg && <p className={msg.ok ? 'css-ok' : 'css-bad'}>{msg.text}</p>}
      </div>
    );
  }

  if (!state.ok) {
    return (
      <div className="css-box">
        <div className="css-title">Stream settings not available</div>
        <p className="css-muted">
          {state.kind === 'unreachable'
            ? 'This camera doesn’t offer ONVIF, or it is turned off on the camera. Its streams are left as they are.'
            : state.error}
        </p>
        <button className="css-btn" onClick={() => load(true)} disabled={!!busy}>
          {busy === 'read' ? 'Checking…' : 'Check again'}
        </button>
        {progress}
      </div>
    );
  }

  const cur = streamsOf(state);
  const roles = ['main', 'sub'].filter(r => cur[r]);
  const applied = state.applied || {};
  const pending = applied.status === 'pending';
  // before → after only for our recommended preset; "Keep previous" swaps it to custom.
  const before = applied.preset === 'recommended' ? applied.before : null;
  const notes  = applied.preset === 'recommended' ? (applied.notes || []) : [];
  // Only a value that really moved is struck through (PRVSK-112). A camera already
  // on the preset used to show "1920×1080 · 15 fps ~~→~~ 1920×1080 · 15 fps" — the
  // same thing crossed out — and offered to "keep previous" settings it never left.
  // Compared on every field the box recorded as "before" (bitrate too, which the
  // line doesn't show), so a bitrate-only change still counts and can be reverted.
  const changed = r => {
    const b = before?.[r], c = cur[r]?.current;
    if (!b || !c) return false;
    return Object.keys(b).some(k => JSON.stringify(b[k]) !== JSON.stringify(c[k]));
  };
  const anyChanged = roles.some(changed);
  // Roles the camera was set to but read back different (backend `kept`).
  const notKept = Object.entries(applied.kept || {}).filter(([, ok]) => !ok).map(([r]) => r);
  const keptOwn = applied.preset === 'kept';          // "Keep as it is" was chosen
  // Not on the recommended settings and nobody has answered yet → ask.
  const asking = !readOnly && !state.matches_recommended && !keptOwn && !applied.applied && !pending;

  if (editing) {
    return (
      <div className="css-box">
        {roles.map(r => (
          <StreamEditor key={r} role={r} value={editing[r]} options={cur[r].options}
            onChange={v => setEditing({ ...editing, [r]: v })} />
        ))}
        <div className="css-actions">
          <button className="css-btn primary" disabled={!!busy} onClick={() => apply(editing)}>
            {busy === 'apply' ? 'Applying…' : 'Apply'}
          </button>
          <button className="css-btn" disabled={!!busy} onClick={() => apply({ preset: 'recommended' })}>
            Use recommended
          </button>
          <button className="css-btn ghost" disabled={!!busy} onClick={() => setEditing(null)}>Cancel</button>
        </div>
        {progress}
        {msg && <p className={msg.ok ? 'css-ok' : 'css-bad'}>{msg.text}</p>}
      </div>
    );
  }

  return (
    <div className={`css-box ${state.matches_recommended ? 'good' : ''}`}>
      <div className="css-title">
        {busy === 'apply' || pending ? 'Optimising streams…'
          : state.matches_recommended ? '✅ Streams optimised'
          : asking ? 'Optimise this camera for Parvekshak?' : 'Stream settings'}
      </div>
      <table className="css-table">
        <tbody>
          {roles.map(r => (
            <tr key={r}>
              <th>{r === 'main' ? 'Main' : 'Sub'}</th>
              <td>
                {changed(r) && line(before[r]) !== line(cur[r].current)
                  && <span className="css-was">{line(before[r])} → </span>}
                {line(cur[r].current)}
                {!state.matches_recommended && cur[r].recommended && (
                  <div className="css-muted">Recommended: {line(cur[r].recommended)}</div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* Only when the camera really reports the recommended values (PRVSK-132) —
          "nothing changed" alone also happens when the camera ignored the push. */}
      {before && !anyChanged && !pending && state.matches_recommended && (
        <p className="css-muted">Already on the recommended settings — nothing was changed.</p>
      )}
      {!pending && notKept.length > 0 && (
        <div className="css-bad">
          <p style={{ margin: 0 }}>The camera didn’t keep these settings:</p>
          <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
            {notKept.map(r => (
              <li key={r}>
                {r === 'main' ? 'Main' : 'Sub'}: asked for {line(applied.applied?.[r])}
                {cur[r] ? <>, the camera still has {line(cur[r].current)}{cur[r].current?.encoding
                  && applied.applied?.[r] && cur[r].current.encoding !== 'H264' ? ` (${cur[r].current.encoding})` : ''}</> : ''}
              </li>
            ))}
          </ul>
          <p style={{ margin: 0 }}>Some cameras only take this change in their own app or web page.</p>
        </div>
      )}
      {notes.length > 0 && (
        <p className="css-muted">Adjusted to what this camera supports: {notes.join('; ')}.</p>
      )}
      {applied.status && !pending && !applied.applied && !state.matches_recommended && (
        <p className="css-muted">Couldn’t apply the recommended settings{applied.error ? `: ${applied.error}` : ''}.</p>
      )}
      {asking && (
        <p className="css-muted">
          Lighter streams keep this box fast and save disk. This changes the settings inside
          the camera — other apps or recorders using it will see the change too.
        </p>
      )}
      {keptOwn && !state.matches_recommended && (
        <p className="css-muted">Kept the camera’s own settings.</p>
      )}
      {!readOnly && !pending && <div className="css-actions">
        {!state.matches_recommended && (
          <button className="css-btn primary" disabled={!!busy} onClick={() => apply({ preset: 'recommended' })}>
            {busy === 'apply' ? 'Optimising…' : 'Optimise'}
          </button>
        )}
        {asking && (
          <button className="css-btn ghost" disabled={!!busy} onClick={keep}>Keep as it is</button>
        )}
        {/* Hand-tuning the encoder is an installer/admin call, not the customer's. */}
        {isAdmin() && <button className="css-btn" disabled={!!busy} onClick={startEdit}>Change</button>}
        {before && anyChanged && (
          <button className="css-btn ghost" disabled={!!busy}
            onClick={() => apply({ main: before.main, sub: before.sub })}>
            Keep camera’s previous settings
          </button>
        )}
      </div>}
      {progress}
      {msg && <p className={msg.ok ? 'css-ok' : 'css-bad'}>{msg.text}</p>}
    </div>
  );
}
